/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { usePermissions, UserContext } from '../../../../../providers/userContextProvider';
import { Typography, Skeleton, Box } from '../../../../1-primative';
import { usePageState } from '../../../../../util/usePageState';
import {
  Table,
  TableHeadCell,
  TableRow,
  TableCell,
  TableBody,
  Badge,
  Card,
  Pagination,
  CardContent,
} from '../../../../2-component';
import { SettingsTitle } from '../../../../3-pattern';
import { translateBackend } from '../../../../../assets/i18n/config';
import { NewSignerType } from './newSignerType';
import { UpdateOrganizationSwitch } from '../../../../4-module/updateOrganizationSwitch/updateOrganizationSwitch';

export const FETCH_SIGNER_TYPES = gql`
  query fetchSignerTypes($input: FetchSignerTypesInput!) {
    fetchSignerTypes(input: $input) {
      signerTypes {
        id
        key
        translatedName {
          en
          fr
        }
        isPrimary
      }
      totalCount
    }
  }
`;

export const SignerTypes = () => {
  const { t } = useTranslation(['settings']);
  const { permissions } = usePermissions();
  const { activeOrganization } = useContext(UserContext);
  const [page, setPage] = usePageState(1, 'page');
  const pageSize = 50;

  const {
    loading, error, data, previousData, refetch,
  } = useQuery(FETCH_SIGNER_TYPES, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
        },
        pagination: {
          sortField: 'translatedName.en', sortDesc: false, perPage: pageSize, offSet: (page - 1) * pageSize,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  if (error) return <Typography>Error</Typography>;

  return (
    <>
      <SettingsTitle title={t('settings:signerTypes.title')} description={t('settings:signerTypes.description')} />
      <Card loading={loading}>
        <CardContent>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box>
              {permissions.includes('write:advanced_organization_settings') && (
                <UpdateOrganizationSwitch
                  field='useParentSignerTypes'
                  label={t('settings:signerTypes.useParentSignerTypes')}
                  organizationId={activeOrganization.id}
                  refetchQueries={[FETCH_SIGNER_TYPES]}
                />
              )}
              </Box>
            <NewSignerType defaultOrg={activeOrganization.id!} afterCreate={() => refetch()} />
          </Box>
        </CardContent>
        <Table>
          <TableBody>
            <TableRow>
              <TableHeadCell>{t('settings:signerTypes.table.key')}</TableHeadCell>
              <TableHeadCell>{t('settings:signerTypes.table.name')}</TableHeadCell>
              <TableHeadCell>{t('settings:signerTypes.table.isPrimary')}</TableHeadCell>
            </TableRow>
            {loading && !previousData && [...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width='100%' /></TableCell>
                <TableCell><Skeleton width='100%' /></TableCell>
                <TableCell><Skeleton width='100%' /></TableCell>
              </TableRow>
            ))}
            {(data || previousData)?.fetchSignerTypes?.signerTypes?.map((signerType: any) => (
              <TableRow hover key={signerType.id}>
                <TableCell>
                  <Typography>{signerType.key}</Typography>
                </TableCell>
                <TableCell>
                  <Typography>{translateBackend(signerType.translatedName)}</Typography>
                </TableCell>
                <TableCell>
                  <Badge
                    label={signerType.isPrimary ? 'Primary' : 'Secondary'}
                    color={signerType.isPrimary ? 'positive' : 'neutral'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          count={Math.ceil(((data || previousData)?.fetchSignerTypes?.totalCount ?? 0) / pageSize)}
          page={page}
          onChange={(_e, newPage) => setPage(newPage)}
          sx={{
            p: 1,
            textAlign: 'right',
            '.MuiPagination-ul': {
              justifyContent: 'end',
            },
          }}
        />
      </Card>
    </>
  );
};
